import { useDialogState } from '@app/src/hooks/mui-hooks'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography, useTheme } from '@mui/material'
import truncateMarkdown from 'markdown-truncate'
import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import Markdown from '../Markdown'

type TruncateWithDialogProps = {
  maxLength?: number
  title?: string
  text: string
}

export default function TruncateWithDialog({ text, maxLength = 250, title }: TruncateWithDialogProps) {
  const [isOpen, openDialog, closeDialog] = useDialogState()

  const { formatMessage } = useIntl()
  const theme = useTheme()

  const handleOpen = useCallback(() => {
    openDialog()
  }, [openDialog])

  const handleClose = useCallback(() => {
    closeDialog()
  }, [closeDialog])

  const { shouldTruncate, displayText } = useMemo(() => {
    const shouldTrunc = text.length > maxLength
    return {
      shouldTruncate: shouldTrunc,
      displayText: shouldTrunc ? truncateMarkdown(text, { limit: maxLength, ellipsis: true }) : text,
    }
  }, [text, maxLength])

  return (
    <>
      <Typography variant="body1" component="span">
        <Markdown>{displayText}</Markdown>
        {shouldTruncate && (
          <Link
            component="button"
            onClick={handleOpen}
            underline="hover"
            sx={{
              color: theme.palette.common.black,
              fontWeight: theme.typography.button.fontWeight,
              textDecoration: 'underline',
              cursor: 'pointer',
              ml: 0.5,
            }}
          >
            {formatMessage({ id: 'general.readMore' })}
          </Link>
        )}
      </Typography>

      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" whiteSpace="pre-wrap">
            <Markdown>{text}</Markdown>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{formatMessage({ id: 'general.close' })}</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
